import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";
import "./MissionReplay.css";
import { fetchMissionReplay } from "../Apis/AdminApis";
import { GoogleMap, LoadScript, Marker, Polyline, useLoadScript } from "@react-google-maps/api";
import {
  FiPlay,
  FiPause,
  FiMaximize,
  FiMinimize,
} from "react-icons/fi";
import {
  MdGpsFixed,
  MdWaterDrop,
  MdSatellite,
  MdHeight,
} from "react-icons/md";
import { GiDeliveryDrone } from "react-icons/gi";
import { BsArrowRight, BsArrowLeft } from "react-icons/bs";
import { TbArrowBackUp } from "react-icons/tb";
import pavamanLogo from "../images/drone.png"; 
const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const mapContainerStyle = {
  height: "100%",
  width: "100%"
};

const mapOptions = {
  mapTypeId: "satellite",
  tilt: 0,
  zoomControl: true,
  mapTypeControl: true,
  scaleControl: true,
  streetViewControl: true,
  rotateControl: true,
  fullscreenControl: true,
  styles: [
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "off" }]
    }
  ]
};

const libraries = ["geometry"];
const createDroneIcon = (isActive = true) => {
  if (!window.google || !window.google.maps) return null;

  return {
    path: "M12 2L15 8H9L12 2ZM12 22L15 16H9L12 22ZM2 12L8 9V15L2 12ZM22 12L16 9V15L22 12Z",
    fillColor: isActive ? "#4450A2" : "#A0A0A0",
    fillOpacity: 1,
    strokeColor: "#FFFFFF",
    strokeWeight: 2,
    scale: 1.2,
    anchor: new window.google.maps.Point(12, 12),
  };
};

const createMarkerIcon = (type) => {
  if (!window.google || !window.google.maps) return null;

  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: type === 'start' ? '#00C853' : '#FF3D00',
    fillOpacity: 1,
    strokeColor: '#FFFFFF',
    strokeWeight: 2,
    scale: 8,
  };
};

const MissionReplay = () => {
  const navigate = useNavigate();
  const location = useLocation();
   const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey,
    libraries,
  });
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedMission, setSelectedMission] = useState(null);
  const [telemetryData, setTelemetryData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [replaySpeed, setReplaySpeed] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const replayTimerRef = useRef(null);
  
  useEffect(() => {
    const loadMissionFromNavigation = async () => {
      if (!location.state?.missionData) {
        setError("No mission selected. Please go back and select a mission to replay.");
        setIsLoading(false);
        return;
      }
      
      const { missionData } = location.state;
      const missionId = missionData?.mission_id;
      const vehicleId = missionData?.vehicle_id;
      const pilotId = missionData?.pilot_id;

      console.log("Replay Payload:", {
        mission_id: missionId,
        vehicle_id: vehicleId,
        pilot_id: pilotId
      });
      
      if (missionData) {
        setSelectedMission(missionData);
      } else {
        setSelectedMission({
          mission_id: missionId,
          vehicle_id: vehicleId,
          pilot_id: pilotId,
          status: 'Replay'
        });
      }
      
try {
  setIsLoading(true);
  setError(null);
  
const payload = {
  mission_id: missionId,
  vehicle_id: vehicleId,
  pilot_id: pilotId,
  downsample_rate: 2
};
  
  const data = await fetchMissionReplay(payload);
  
  // Session expiry check
  if (data?.error) {
    if (data?.error?.includes("Unauthorized")) {
      setError("Session expired, redirecting to login page");
      setTimeout(() => {
        navigate("/login");
      }, 2000);
      return;
    }
    setError(data.error || "Failed to load mission telemetry data");
    return;
  }
  
  setTelemetryData(data);
  setIsPlaying(false);
  setCurrentIndex(0); 
  clearInterval(replayTimerRef.current);
} catch (err) {
  console.error("Failed to load mission telemetry:", err);
  
  // Check for unauthorized error in catch block
  if (err?.response?.status === 401 || err?.response?.data?.error?.includes("Unauthorized")) {
    setError("Session expired, redirecting to login page");
    setTimeout(() => {
      navigate("/login");
    }, 2000);
    return;
  }
  
  setError("Failed to load mission telemetry data");
} finally {
  setIsLoading(false);
}
    };
    
    loadMissionFromNavigation();
  }, [location.state]);

  useEffect(() => {
    if (mapRef.current && telemetryData?.telemetry?.length > 0 && mapLoaded) {
      const bounds = new window.google.maps.LatLngBounds();
      telemetryData.telemetry.forEach(point => {
        bounds.extend({ lat: point.lat, lng: point.lng });
      });
      mapRef.current.fitBounds(bounds);
    }
  }, [telemetryData, mapLoaded]);
  
const handlePlayPause = () => {
  if (!telemetryData || !telemetryData.telemetry) return;

  if (isPlaying) {
    clearInterval(replayTimerRef.current);
    setIsPlaying(false);
  } else {
    if (currentIndex >= telemetryData.telemetry.length - 1) {
      setCurrentIndex(0);
    }

    clearInterval(replayTimerRef.current);
    setIsPlaying(true);

    replayTimerRef.current = setInterval(() => {
      setCurrentIndex(prev => {
        if (prev >= telemetryData.telemetry.length - 1) {
          clearInterval(replayTimerRef.current);
          setIsPlaying(false);
          return 0;
        }
        return prev + 1;
      });
    }, 1000 / replaySpeed);
  }
};
  
  const handleReset = () => {
    setCurrentIndex(0);
    if (isPlaying) {
      clearInterval(replayTimerRef.current);
      setIsPlaying(false);
    }
  };
  
  const handleSkipForward = () => {
    if (!telemetryData || !telemetryData.telemetry) return;
    setCurrentIndex(prev => Math.min(prev + 10, telemetryData.telemetry.length - 1));
  };
  
  const handleSkipBackward = () => {
    setCurrentIndex(prev => Math.max(prev - 10, 0));
  };
  
  const handleSpeedChange = (speed) => {
    setReplaySpeed(speed);
    if (isPlaying) {
      clearInterval(replayTimerRef.current);
      replayTimerRef.current = setInterval(() => {
        setCurrentIndex(prev => {
          if (prev >= telemetryData.telemetry.length - 1) {
            clearInterval(replayTimerRef.current);
            setIsPlaying(false);
             return prev;
          }
          return prev + 1;
        });
      }, Math.max(1000 / speed, 50));
    }
  };
  
const handleGoBack = () => {
  if (location.state?.from === "mission-events") {
    navigate("/admin/mission/events");
  } 
  else if (location.state?.from === "all-flights") {
    navigate("/admin/all-flights", {
      state: {
        fromPage: location.state.fromPage,
        filters: location.state.filters || {}
      }
    });
  }
else if (location.state?.from === "pilot-drone-quick-search") {
  navigate("/admin/pilot-drone-quick-search", {
    state: {
      pilotPage: location.state.pilotPage,
      missionPage: location.state.missionPage,
      filters: location.state.filters,
    },
  });
}
  else if (location.state?.from === "dashboard") {
navigate("/admin/dashboard", {
  state: {
    dashboardFilters: location.state.dashboardFilters
  }
});
  }
  else {
    navigate(-1);
  }
};

  
  const missionPath = telemetryData?.telemetry?.map(point => ({ lat: point.lat, lng: point.lng })) || [];
  const currentPoint = telemetryData?.telemetry?.[currentIndex] || null;
  
  const formatTime = (timestamp) => {
    if (!timestamp) return "00:00";
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };
  
const progressPercentage =
  telemetryData?.telemetry?.length > 0
    ? ((currentIndex + 1) / telemetryData.telemetry.length) * 100
    : 0;
  
  const exportTelemetry = () => {
    if (!telemetryData) return;
    
    const csvContent = [
      "Timestamp,Latitude,Longitude,Altitude,Speed,Satellites,HDOP,Spray Rate,Flow Pulse",
      ...telemetryData.telemetry.map(point => 
        `${point.ts},${point.lat},${point.lng},${point.alt},${point.speed},${point.satellites},${point.hdop},${point.spray_rate_lpm},${point.flow_pulse}`
      )
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mission_replay_${telemetryData.mission_id}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    return () => {
      if (replayTimerRef.current) {
        clearInterval(replayTimerRef.current);
      }
    };
  }, []);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    setMapLoaded(true);
    // setIsMapLoading(false);
  }, []);
  const onMapScriptError = useCallback(() => {
    setError("Failed to load Google Maps. Please check your internet connection and try again.");
  }, []);

if (loadError) {
  return (
    <div className={`mission-replay-wrapper ${isFullscreen ? 'fullscreen' : ''}`}>
      <Sidebar />
      <div className="mission-replay-content">
        <div className="error-container">
          <div className="error-icon">🗺️</div>
          <h3>Google Maps Failed to Load</h3>
          <p>Please check your internet connection and refresh the page.</p>
          <button onClick={() => window.location.reload()} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    </div>
  );
}

if (!isLoaded) {
  return (
    <div className="custom-loading-container">
      <img src={pavamanLogo} alt="Loading" className="spinning-logo" />
      <p className="loading-text">Loading google maps...</p>
    </div>
  );
}
  
 if (error) {
  // Check if error is session expiry related
  const isSessionExpired = error.includes("Session expired");
  
  return (
    <div className={`mission-replay-wrapper ${isFullscreen ? 'fullscreen' : ''}`}>
      <Sidebar />
      <div className="mission-replay-content">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h3>{isSessionExpired ? "Session Expired" : "Error Loading Mission"}</h3>
          <p>{error}</p>
          {!isSessionExpired && (
            <button 
              onClick={() => window.location.reload()} 
              className="retry-btn"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
  
  return (
    <div className={`mission-replay-wrapper ${isFullscreen ? 'fullscreen' : ''}`}>
      <Sidebar />
      
      <div className="mission-replay-content">
        <div className="mr-header">
          <div className="mr-header-left">
            <button className="back-nav-btn" onClick={handleGoBack}>
              ← Back
            </button>
            <h1>
              <GiDeliveryDrone className="header-icon" />
              Mission Replay
            </h1>
            <div className="mission-tags">
              <p className="mission-id-badge">
                {telemetryData?.mission_id || selectedMission?.mission_id || 'N/A'}
              </p>
              <p className="vehicle-id-badge">
                <GiDeliveryDrone size={14} />
                {telemetryData?.drone_id || selectedMission?.vehicle_id || 'N/A'}
              </p>
            </div>
          </div>
          
          <div className="mr-header-right">
            <div className="status-indicator">
              <span className="status-dot"></span>
              <span className="status-text">
                {telemetryData?.mission_status === 4 ? 'Completed' : 'Replay Mode'}
              </span>
            </div>
            
            <button 
              className="fullscreen-btn"
              onClick={() => setIsFullscreen(!isFullscreen)}
            >
              {isFullscreen ? <FiMinimize size={18} /> : <FiMaximize size={18} />}
            </button>
          </div>
        </div>

        <div className="mr-single-grid">
          <div className="mr-stats-bar">
            <div className="mr-stat-block">
              <span className="mr-stat-label">Drone Name</span>
              <span className="mr-stat-value">{telemetryData?.drone_name || 'N/A'}</span>
            </div>
            <div className="mr-stat-block">
              <span className="mr-stat-label">Pilot</span>
              <span className="mr-stat-value">{telemetryData?.pilot_name || 'N/A'}</span>
            </div>
            <div className="mr-stat-block">
              <span className="mr-stat-label">Telemetry Points</span>
              <span className="mr-stat-value">
  {telemetryData?.total_points || 0}
</span>
            </div>
          </div>

          <div className="mr-map-area">
            <div className="map-container">
               <GoogleMap
  key={telemetryData?.mission_id}   
  mapContainerStyle={mapContainerStyle}
  center={
    currentPoint
      ? { lat: currentPoint.lat, lng: currentPoint.lng }
      : missionPath[0] || { lat: 20.5937, lng: 78.9629 }
  }
  zoom={currentPoint ? 18 : (missionPath.length ? 12 : 6)}
  options={mapOptions}
  onLoad={onMapLoad}
>
                  {mapLoaded && telemetryData && (
                    <>
                      {missionPath.length > 0 && window.google &&  (
                        <Polyline
                          path={missionPath}
                          options={{
                            strokeColor: "#4450A2",
                            strokeOpacity: 0.8,
                            strokeWeight: 4,
                            icons: [{
                              icon: {
                              path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW || null,
                                scale: 2,
                                strokeColor: "#4450A2"
                              },
                              offset: '50%',
                              repeat: '50px'
                            }]
                          }}
                        />
                      )}
                      
                      {missionPath.length > 0 && window.google &&  (
                        <Marker
                          position={missionPath[0]}
                          icon={createMarkerIcon('start')}
                          title="Mission Start"
                        />
                      )}
                      
                      {missionPath.length > 1 && window.google && (
                        <Marker
                          position={missionPath[missionPath.length - 1]}
                          icon={createMarkerIcon('end')}
                          title="Mission End"
                        />
                      )}
                      
                      {currentPoint && window.google && (
                        <Marker
                          position={{ lat: currentPoint.lat, lng: currentPoint.lng }}
                          icon={createDroneIcon(true)}
                          title={`Drone ${telemetryData?.drone_name || telemetryData?.drone_id}`}
                        />
                      )}
                    </>
                  )}
                </GoogleMap>
            </div>

            <div className="replay-controls-overlay">
              <div className="progress-container">
                <div className="progress-bar" style={{ width: `${progressPercentage}%` }}></div>
              </div>
              
              <div className="replay-controls">
                <button className="control-btn" onClick={handleReset} disabled={!telemetryData}>
                  <TbArrowBackUp size={20} />
                </button>
                <button className="control-btn" onClick={handleSkipBackward} disabled={!telemetryData || currentIndex === 0}>
                  <BsArrowLeft size={20} />
                </button>
                <button className={`play-btn ${isPlaying ? 'playing' : ''}`} onClick={handlePlayPause} disabled={!telemetryData}>
                  {isPlaying ? <FiPause size={28} /> : <FiPlay size={28} />}
                </button>
                <button className="control-btn" onClick={handleSkipForward} disabled={!telemetryData || currentIndex >= (telemetryData?.telemetry?.length - 1)}>
                  <BsArrowRight size={20} />
                </button>
                <div className="speed-selector">
                  {[1, 1.5, 1.75, 2, 4, 8].map(speed => (
                    <button
                      key={speed}
                      className={`speed-btn ${replaySpeed === speed ? 'active' : ''}`}
                      onClick={() => handleSpeedChange(speed)}
                    >
                      {speed}x
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mr-telemetry-grid">
            <div className="telemetry-card">
              <div className="telemetry-header">
                <MdGpsFixed className="telemetry-icon" />
                <span>Current Position</span>
              </div>
              <div className="telemetry-content">
                <div className="coord-row">
                  <span>Lat:</span>
                  <span>{currentPoint?.lat?.toFixed(6) || '0.000000'}</span>
                </div>
                <div className="coord-row">
                  <span>Lng:</span>
                  <span>{currentPoint?.lng?.toFixed(6) || '0.000000'}</span>
                </div>
                <div className="coord-row">
  <span>Time:</span>
  <span>{formatTime(currentPoint?.ts)}</span>
</div>
              </div>
            </div>

            <div className="telemetry-card">
              <div className="telemetry-header">
                <MdHeight className="telemetry-icon" />
                <span>Altitude & Speed & Voltage</span>
              </div>
              <div className="telemetry-content">
                <div className="coord-row">
                  <span>Altitude:</span>
                  <span>{currentPoint?.alt?.toFixed(1) || '0'} m</span>
                </div>
                <div className="coord-row">
                  <span>Speed:</span>
                  <span>{currentPoint?.speed?.toFixed(1) || '0'} m/s</span>
                </div>
                 <div className="coord-row">
                  <span>Voltage:</span>
                  <span>{currentPoint?.voltage?.toFixed(1) || '0'} v</span>
                </div>
              </div>
            </div>

            <div className="telemetry-card">
              <div className="telemetry-header">
                <MdSatellite className="telemetry-icon" />
                <span>GPS Status</span>
              </div>
              <div className="telemetry-content">
                <div className="coord-row">
                  <span>Satellites:</span>
                  <span>{currentPoint?.satellites || '0'}</span>
                </div>
                <div className="coord-row">
                  <span>HDOP:</span>
                  <span>{currentPoint?.hdop?.toFixed(2) || '0.00'}</span>
                </div>
              </div>
            </div>

            <div className="telemetry-card">
              <div className="telemetry-header">
                <MdWaterDrop className="telemetry-icon" />
                <span>Spray System</span>
              </div>
              <div className="telemetry-content">
                <div className="coord-row">
                  <span>Spray Rate:</span>
                  <span>{currentPoint?.spray_rate_lpm?.toFixed(1) || '0'} L/min</span>
                </div>
                <div className="coord-row">
                  <span>Flow Pulse:</span>
                  <span>{currentPoint?.flow_pulse || '0'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissionReplay;